import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Eye, Send, Lock, Loader2 } from 'lucide-react';
import { useRfq } from '@/features/compras/hooks/useRfq';
import type { StatusRfq } from '@/features/compras/types';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { BarraFiltros } from '@/components/BarraFiltros';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';

// USA-COM-13: período de emissão e busca por nº/título.
// O período usa o PeriodoFiltro padrão das listas (estado local, vai ao servidor como dtInicio/dtFim).
const PADROES = {
  busca: '',
  status: 'todos',
  pagina: 1,
  tamanhoPagina: 20,
};

const statusConfig: Record<StatusRfq, { label: string; className: string }> = {
  Rascunho:    { label: 'Rascunho',     className: 'bg-gray-500/10 text-gray-600'     },
  Enviada:     { label: 'Enviada',      className: 'bg-blue-500/10 text-blue-600'     },
  EmResposta:  { label: 'Em Resposta',  className: 'bg-yellow-500/10 text-yellow-600' },
  Encerrada:   { label: 'Encerrada',    className: 'bg-orange-500/10 text-orange-600' },
  Adjudicada:  { label: 'Adjudicada',   className: 'bg-green-500/10 text-green-600'   },
  Cancelada:   { label: 'Cancelada',    className: 'bg-red-500/10 text-red-600'       },
};

const Cotacoes = () => {
  const navigate = useNavigate();
  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });
  // Período e ordem vão ao servidor (padrão de listas da v118): a lista cresce sem limite com o tempo.
  const voltarPagina1 = () => setFiltros({ pagina: 1 });
  const periodo   = usePeriodo('30dias', voltarPagina1);
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'prazo'>('emissao', true, voltarPagina1);

  const { rfqs, total, totalPaginas, porStatus, comRespostas, isLoading, isFetching,
          enviarRfq, encerrarRfq, isEnviando, isEnccerrando } =
    useRfq({
      pagina: pag.pagina,
      tamanhoPagina: pag.tamanhoPagina,
      status: filtros.status === 'todos' ? undefined : filtros.status as StatusRfq,
      filtros: {
        busca:       filtros.busca || undefined,
        ...periodo.intervalo,
        ordenarPor:  ordenacao.ordenarPor,
        decrescente: ordenacao.decrescente,
      },
    });

  // Contagens do conjunto filtrado, calculadas no servidor — não só desta página.
  const emAberto   = Object.entries(porStatus ?? {})
    .filter(([st]) => st !== 'Adjudicada' && st !== 'Cancelada')
    .reduce((s, [, qtd]) => s + qtd, 0);
  const responderam = comRespostas ?? 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Cotações</h1>
          <p className="text-muted-foreground mt-1">Pedidos de cotação — Request for Quotation (RFQ)</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/cotacoes/nova')}>
          <Plus className="h-4 w-4" />Nova Cotação
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />Em Aberto
            </CardTitle>
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{emAberto}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Com Respostas</CardTitle>
          </CardHeader>
          <CardContent><div className="text-2xl font-bold text-yellow-600">{responderam}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">No período e filtros selecionados</p>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº ou título da cotação..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={() => { limparFiltros(); periodo.setPeriodo('30dias'); }}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-52" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {Object.entries(statusConfig).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Emissão</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
          </div>
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>RFQ #</CabecalhoOrdenavel>
                <TableHead>Título</TableHead>
                <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                <CabecalhoOrdenavel coluna="prazo" ordenacao={ordenacao} decrescentePadrao={false}>Prazo</CabecalhoOrdenavel>
                <TableHead>Fornecedores</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                </TableRow>
              ) : rfqs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma cotação neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma cotação encontrada'}
                  </TableCell>
                </TableRow>
              ) : (
                rfqs.map(r => {
                  const cfg = statusConfig[r.status] ?? { label: String(r.status), className: '' };
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono font-bold">#{r.numero}</TableCell>
                      <TableCell className="max-w-xs truncate">{r.titulo}</TableCell>
                      <TableCell>{new Date(r.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        {r.dtPrazo ? new Date(r.dtPrazo).toLocaleDateString('pt-BR') : '—'}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {r.totalResponderam}/{r.totalFornecedores}
                          <span className="text-muted-foreground ml-1">responderam</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={cfg.className}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" title="Ver detalhes"
                            onClick={() => navigate(`/compras/cotacoes/${r.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {r.status === 'Rascunho' && (
                            <Button size="sm" variant="ghost" title="Enviar convites"
                              disabled={isEnviando}
                              onClick={() => enviarRfq(r.id)}>
                              <Send className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                          {(r.status === 'Enviada' || r.status === 'EmResposta') && (
                            <Button size="sm" variant="ghost" title="Encerrar cotação"
                              disabled={isEnccerrando}
                              onClick={() => encerrarRfq(r.id)}>
                              <Lock className="h-4 w-4 text-orange-500" />
                            </Button>
                          )}
                          {r.status === 'Encerrada' && (
                            <Button size="sm" variant="ghost" title="Ver comparativo"
                              onClick={() => navigate(`/compras/cotacoes/${r.id}/comparativo`)}>
                              <FileText className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />
    </div>
  );
};

export default Cotacoes;
